import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import {
  Users, Calendar, ClipboardList, Vote, Link2, BarChart3,
  Settings, Globe, UserPlus, Handshake, ChevronLeft, ChevronRight,
  PartyPopper, X,
} from 'lucide-react';

const STEPS = [
  {
    icon: PartyPopper,
    iconBg: 'from-blue-500 to-blue-600',
    title: 'Bem-vindo ao Sistema!',
    description: 'Este é o seu painel de gestão de membros, eventos e muito mais. Vamos apresentar cada módulo para que você aproveite ao máximo.',
    details: [
      'Gerencie membros, auxiliares e administradores',
      'Controle eventos, presenças e relatórios',
      'Tudo num único lugar, acessível de qualquer dispositivo',
    ],
  },
  {
    icon: Users,
    iconBg: 'from-emerald-500 to-emerald-600',
    title: 'Pessoas',
    description: 'O coração do sistema. Aqui você gerencia todos os membros do grupo.',
    details: [
      'Cadastre membros manualmente ou importe via Excel',
      'Aprove ou rejeite cadastros pendentes',
      'Atribua membros a funções de serviço',
      'Adicione anotações e acompanhe o histórico',
      'Registre dados dos pais (nome e WhatsApp)',
      'Inative membros com motivo registrado',
    ],
  },
  {
    icon: Calendar,
    iconBg: 'from-orange-500 to-orange-600',
    title: 'Eventos e Presença',
    description: 'Crie eventos, registre presenças e acompanhe a participação dos membros.',
    details: [
      'Crie diferentes tipos de evento (culto, reunião, etc.)',
      'Registre a presença individualmente ou em massa',
      'Feche e reabra eventos conforme necessário',
      'Visualize o histórico de presença por membro',
    ],
  },
  {
    icon: Handshake,
    iconBg: 'from-violet-500 to-violet-600',
    title: 'Auxiliares',
    description: 'Gerencie os auxiliares do grupo com visualização por gênero.',
    details: [
      'Veja auxiliares agrupados em "Meninos" e "Meninas"',
      'Acompanhe os aniversariantes do mês',
      'Envie mensagens de parabéns via WhatsApp',
    ],
  },
  {
    icon: ClipboardList,
    iconBg: 'from-cyan-500 to-cyan-600',
    title: 'Reuniões e Tarefas',
    description: 'Agende reuniões, defina pautas, gere atas e atribua tarefas.',
    details: [
      'Crie reuniões com data, hora e pautas',
      'Registre a ata durante ou após a reunião',
      'Vincule reuniões a eventos para controle de presença',
      'Crie tarefas com responsáveis e prazos',
      'Acompanhe o status das tarefas (pendente/concluída)',
    ],
  },
  {
    icon: Vote,
    iconBg: 'from-pink-500 to-pink-600',
    title: 'Votações',
    description: 'Crie enquetes e votações para decisões do grupo.',
    details: [
      'Crie votações com múltiplas opções',
      'Envie para os auxiliares responderem',
      'Acompanhe os resultados em tempo real',
      'Encerre e reabra votações conforme necessário',
    ],
  },
  {
    icon: Link2,
    iconBg: 'from-indigo-500 to-indigo-600',
    title: 'Links (Linktree)',
    description: 'Crie uma página pública com os links importantes do grupo.',
    details: [
      'Adicione links internos e externos',
      'Personalize cores, ícones e bordas de cada link',
      'Configure a aparência da página (fundo, fonte, bio)',
      'Compartilhe o URL público com os membros',
    ],
  },
  {
    icon: BarChart3,
    iconBg: 'from-amber-500 to-amber-600',
    title: 'Relatórios e Dashboard',
    description: 'Acompanhe métricas e gráficos para uma visão completa do grupo.',
    details: [
      'Dashboard com visão geral de membros e eventos',
      'Relatórios de engajamento por pessoa',
      'Relatórios por tipo de evento e auxiliar',
      'Gráficos interativos de participação',
    ],
  },
  {
    icon: Globe,
    iconBg: 'from-teal-500 to-teal-600',
    title: 'Cadastro Público',
    description: 'Permita que novos membros se cadastrem e atualizem seus dados online.',
    details: [
      'Link público para novos cadastros com formulário em etapas',
      'Atualização de dados via verificação por SMS',
      'Verificação de duplicados automática',
      'Perguntas anônimas via link público',
    ],
  },
  {
    icon: Settings,
    iconBg: 'from-slate-500 to-slate-600',
    title: 'Configurações',
    description: 'Personalize o sistema conforme as necessidades do seu grupo.',
    details: [
      'Gerencie tipos de evento e motivos',
      'Ative ou desative módulos (Addons)',
      'Administre usuários e permissões',
      'Importe e exporte dados (Backup)',
      'Gerencie os links da página pública',
    ],
  },
];

export default function OnboardingWizard({ open, onClose, onComplete }) {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  const handleComplete = () => {
    setStep(0);
    onComplete();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg p-0 overflow-hidden gap-0 [&>button.absolute]:hidden">
        <DialogTitle className="sr-only">Tutorial do Sistema</DialogTitle>
        {/* Header */}
        <div className={`bg-gradient-to-br ${current.iconBg} px-6 pt-8 pb-6 text-white relative`}>
          <button onClick={onClose} className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors" data-testid="onboarding-close">
            <X className="w-5 h-5" />
          </button>
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4">
            <Icon className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-xl font-bold">{current.title}</h2>
          <p className="text-sm text-white/80 mt-1 leading-relaxed">{current.description}</p>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          <ul className="space-y-2.5">
            {current.details.map((detail, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-br ${current.iconBg} mt-1.5 flex-shrink-0`} />
                {detail}
              </li>
            ))}
          </ul>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex items-center justify-between">
          {/* Progress dots */}
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <button key={i} onClick={() => setStep(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === step ? 'bg-blue-600 w-5' : 'bg-slate-200 hover:bg-slate-300'}`}
                data-testid={`onboarding-dot-${i}`} />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="outline" size="sm" onClick={() => setStep(s => s - 1)} data-testid="onboarding-prev">
                <ChevronLeft className="w-4 h-4 mr-1" />Anterior
              </Button>
            )}
            {isLast ? (
              <Button size="sm" onClick={handleComplete} className="bg-blue-600 hover:bg-blue-700" data-testid="onboarding-finish">
                Concluir
              </Button>
            ) : (
              <Button size="sm" onClick={() => setStep(s => s + 1)} className="bg-blue-600 hover:bg-blue-700" data-testid="onboarding-next">
                Próximo<ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
